import { formatAmount, getCurrencySymbol } from '@/shared/lib/financial';
import type { Currency } from '@/shared/types/currency';

/**
 * Renders a monetary amount with its currency symbol, keeping the minus sign
 * outside the symbol ("-$1.234,56" rather than "$-1.234,56").
 *
 * The digits are grouped by `formatAmount`, so an amount reads "235.146,79"
 * instead of the bare "235146.79" that `toFixed` produced. Callers that parse
 * the string back into a number must do so BEFORE this point: the grouped form
 * is for display only.
 */
export function parseNegativeNumber(
    value: number,
    currency?: Nullable<Currency>,
    decimals: number = 2,
): string {
    const formattedValue = formatAmount(value, decimals);
    const absoluteValue = formatAmount(Math.abs(value), decimals);

    if (currency) {
        const symbol = getCurrencySymbol(currency);

        return value >= 0
            ? `${symbol}${formattedValue}`
            : `-${symbol}${absoluteValue}`;
    }

    return value >= 0 ? `$${formattedValue}` : `-$${absoluteValue}`;
}
